import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { NetworkService } from '../../../core/services/network';
import { OfflineWriteService } from '../../../core/services/offline-write';
import { OutboxService } from '../../../core/services/outbox';

/**
 * Indicateur d'état réseau — pastille compacte posée en bas de l'écran.
 *
 * Elle était auparavant une barre pleine largeur en `fixed top-0` portant la
 * classe `.pt-safe` (`styles.scss` : `max(env(safe-area-inset-top), 3rem)`), ce
 * qui lui donnait ~70 px de haut et lui faisait recouvrir l'en-tête de chaque
 * écran. Un état permanent ne doit pas occuper la zone du titre : il se pose
 * là où rien ne vit, au-dessus de la nav flottante.
 *
 * Le décalage bas de 96 px la place au-dessus de la nav (`safe + 22 px`, ~56 px
 * de haut) ET des barres d'action fixes des écrans de saisie (`py-4 pb-safe`,
 * ~76 px). `pointer-events: none` garantit qu'elle n'intercepte jamais un tap,
 * et `z-30` la laisse sous la nav et les snackbars (toutes deux en `z-50`).
 */
@Component({
  selector: 'app-offline-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!network.isOnline()) {
    <div
      class="pointer-events-none fixed inset-x-0 z-30 flex justify-center px-4"
      style="bottom: calc(env(safe-area-inset-bottom, 0px) + 96px)"
    >
      <div
        class="inline-flex max-w-full items-center gap-[7px] rounded-full border border-white/10 py-1.5 pr-3.5 pl-3 shadow-[0_14px_34px_-12px_rgba(0,0,0,0.7)] backdrop-blur-[16px]"
        style="background: rgba(20,21,36,0.92); animation: te-rise 0.26s var(--ease-ios) both"
        role="alert"
        aria-live="assertive"
        aria-label="Mode hors ligne — les données affichées proviennent du cache"
      >
        <span class="material-icons-round shrink-0 text-[15px] text-amber-400" aria-hidden="true"
          >cloud_off</span
        >
        <span class="truncate text-[12px] font-semibold text-amber-400">
          Mode hors ligne @if (pending() > 0) {<span class="text-amber-300"
            >&middot; {{ pendingLabel() }}</span
          >}
        </span>
      </div>
    </div>
    } @else if (pending() > 0) {
    <!-- De retour en ligne, mais des écritures partent encore vers le serveur. -->
    <div
      class="pointer-events-none fixed inset-x-0 z-30 flex justify-center px-4"
      style="bottom: calc(env(safe-area-inset-bottom, 0px) + 96px)"
    >
      <div
        class="inline-flex max-w-full items-center gap-[7px] rounded-full border border-white/10 py-1.5 pr-3.5 pl-3 shadow-[0_14px_34px_-12px_rgba(0,0,0,0.7)] backdrop-blur-[16px]"
        style="background: rgba(20,21,36,0.92); animation: te-rise 0.26s var(--ease-ios) both"
        role="status"
        aria-live="polite"
      >
        <span
          class="material-icons-round shrink-0 text-[15px] text-emerald-400"
          style="animation: te-spin 1.1s linear infinite"
          aria-hidden="true"
          >sync</span
        >
        <span class="truncate text-[12px] font-semibold text-emerald-400">
          Synchronisation &middot; {{ pendingLabel() }}
        </span>
      </div>
    </div>
    }
  `,
})
export class OfflineBanner {
  readonly network = inject(NetworkService);
  private offlineWrite = inject(OfflineWriteService);
  private outbox = inject(OutboxService);

  /**
   * Deux files coexistent, et l'utilisateur n'a pas à connaître la différence :
   * celle de Firestore pour les écritures directes, la nôtre pour les appels de
   * Cloud Functions (Cotisations, Dépenses).
   */
  protected readonly pending = computed(
    () => this.offlineWrite.pendingCount() + this.outbox.pendingCount()
  );
  protected readonly pendingLabel = computed(() => {
    const n = this.pending();
    return `${n} action${n > 1 ? 's' : ''} en attente`;
  });
}
